import React, { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ArrowsCounterClockwise, CaretRight, DownloadSimple } from 'phosphor-react';

import { Report } from 'graphql-api';
import Spinner from 'components/spinner';
import { DATA_IMPORT_STEP } from 'constants/report-steps';
import { HRIS_GUSTO_PROVIDER } from 'constants/hris-providers';
import { getHRISDataInsights, resyncData } from 'api/hris-data-insights';
import { navigateAndStoreNextStep } from 'utils/navigation';

function HRISDataInsights({ report }: { report: Report }): JSX.Element {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isResyncingData, setIsResyncingData] = useState(false);
  const [dataInsights, setDataInsights] = useState<{
    missingRaceEthnicity: number;
    missingWorkLocation: number;
    missingSexCategory: number;
    missingRequiredField: number;
  } | null>(null);

  const handleDownloadInputFile = (): void => {
    void navigateAndStoreNextStep(report, DATA_IMPORT_STEP, ((pathname: string): void => {
      navigate(pathname, { state: { downloadStarted: true } });
    }) as NavigateFunction);
  };

  function handleContinueClick(): void {
    void navigateAndStoreNextStep(report, DATA_IMPORT_STEP, navigate);
  }

  useEffect(() => {
    void (async () => {
      const res = await getHRISDataInsights(report.id);
      if (res != null && !Object.values(res).every((value) => value === 0)) {
        setDataInsights(res);
      }
      setIsLoading(false);
    })();
  }, []);

  function handleRefreshData(): void {
    void (async () => {
      setIsResyncingData(true);
      const res = await resyncData(report.id);
      if (res) {
        window.location.reload();
      } else {
        alert('Sorry there was an issue refreshing data from your HRIS/Payroll provider.');
      }
    })();
  }

  return (
    <>
      <div className="max-w-6xl m-auto bg-white shadow mt-8 py-16 px-14">
        <div className="pb-6">
          <div className="text-2xl font-medium leading-6 text-gray-900">
            HR sync was successful, but you are missing some data required for the report.
          </div>
          {dataInsights !== null && (
            <div className="text-base text-gray-700 mt-2 w-4/5">
              The California pay data report requires certain fields that&nbsp;
              {Object.values(dataInsights).every((value) => value === 0)
                ? 'we were unable to access from '
                : 'are not populated in '}
              your HRIS/Payroll system. After reviewing your data for completeness, we’ve uncovered
              the following missing item(s) which are required to comply with the California pay
              data report.
            </div>
          )}
        </div>

        <div className="bg-gray-100 text-gray-900 rounded px-6 py-8 mb-16">
          {isLoading ? (
            <div className="text-center py-16">
              <Spinner className="text-gray-500" size={48} />
            </div>
          ) : (
            dataInsights !== null && (
              <>
                <ul className="list-disc px-6">
                  {dataInsights?.missingRaceEthnicity > 0 && (
                    <li>
                      <span className="font-semibold" data-testid="missing-race-ethnicity">
                        {dataInsights?.missingRaceEthnicity}
                      </span>
                      &nbsp;employees are missing race & ethnicity category
                    </li>
                  )}
                  {dataInsights?.missingWorkLocation > 0 && (
                    <li>
                      <span className="font-semibold" data-testid="missing-work-location">
                        {dataInsights?.missingWorkLocation}
                      </span>
                      &nbsp;employees are missing work location (not assigned to an establishment)
                    </li>
                  )}
                  {dataInsights?.missingSexCategory > 0 && (
                    <li>
                      <span className="font-semibold" data-testid="missing-sex-category">
                        {dataInsights?.missingSexCategory}
                      </span>
                      &nbsp;employees are missing a recognized sex category (male, female,
                      non-binary)*
                    </li>
                  )}
                  {dataInsights?.missingRequiredField > 0 && (
                    <li>
                      <span className="font-semibold" data-testid="missing-required-field">
                        {dataInsights?.missingRequiredField}
                      </span>
                      &nbsp;employees are missing at least one other required field
                    </li>
                  )}
                  {report.hrisProvider !== HRIS_GUSTO_PROVIDER.integration && (
                    <li>
                      <span className="font-semibold">Note:&nbsp;</span>
                      We were unable to access to W-2 (box 5) for 2022, and hours worked in 2022
                      from your HRIS/Payroll provider, so you’ll need to supplement this information
                      using option 1 or 2 below.
                    </li>
                  )}
                </ul>
              </>
            )
          )}
        </div>

        <div className="pb-6 border-b border-gray-200">
          <div className="text-2xl font-medium leading-6 text-gray-900">How can I fix it?</div>
          <div className="text-base text-gray-700 mt-2 w-2/3">
            We recommend any of the three following options to complete your data and prepare it for
            a successful CA pay data filing.
          </div>
        </div>
        <div className="py-6 flex justify-between items-center border-b border-gray-200">
          <div className="flex-1">
            <div className="text-xl text-black">
              Option 1 – (Recommended) Download a spreadsheet, complete it offline, then re-upload
            </div>
            <div className="text-sm text-gray-700 pl-24">
              You’ll also be able to download a source file which can be used to update your system
              of record (HRIS/payroll system).
            </div>
          </div>
          <a className="btn-primary cursor-pointer ml-8" onClick={handleDownloadInputFile}>
            <DownloadSimple size={18} className="mr-1" />
            &nbsp;Download file & continue
          </a>
        </div>
        <div className="py-6 flex justify-between items-center border-b border-gray-200">
          <div className="flex-1">
            <div className="text-xl text-black">
              Option 2 – Complete your data within our interface
            </div>
            <div className="text-sm text-gray-700 pl-24">
              Our interface makes editing your data quick and easy, right here in OpenCompliance.
              You can still download the spreadsheet if needed from the next step by clicking
              “Export”
            </div>
          </div>
          <a className="btn-secondary cursor-pointer ml-8" onClick={handleContinueClick}>
            Continue
            <CaretRight size={18} className="ml-1" />
          </a>
        </div>
        <div className="py-6 flex justify-between items-center border-b border-gray-200">
          <div className="flex-1">
            <div className="text-xl text-black">
              Option 3 – Fix in the source system, then refresh your data*
            </div>
            <div className="text-sm text-gray-700 pl-24">
              If you want to make updates directly in your payroll system or HRIS, you can make
              updates there and then refresh the data. Note: you’ll need to use option 2 or 3 to
              supplement W-2 (box 5), and hours worked data.
            </div>
          </div>
          <a className="btn-secondary cursor-pointer ml-8" onClick={handleRefreshData}>
            {isResyncingData ? (
              <Spinner size={18} className="mr-1" />
            ) : (
              <ArrowsCounterClockwise size={18} className="mr-1" />
            )}
            &nbsp;Reconnect
          </a>
        </div>

        <div className="pt-12 text-sm text-gray-700 w-2/3">
          *Some companies prefer to keep gender/sex in their HRIS/Payroll system differently than
          what California requires — e.g. additional fields other than male, female, or non-binary.
          If this sounds like your company, you may want to choose option 2 or 3 to complete your
          report.
        </div>
      </div>
    </>
  );
}

export default HRISDataInsights;
